import {
  INITIAL_DEPARTMENT_FORM,
  INITIAL_DESIGNATION_FORM,
  INITIAL_EMPLOYEE_FORM,
} from "../assets/instateamMasterConfig";
import { normalizeAdminPermissions } from "./adminPermissions";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://instaport-e6fnbjbhhtbbc7ca.southindia-01.azurewebsites.net";
export const STORAGE_KEY = "instateam-master-session";

const ADMIN_THEME_PRESETS = {
  warm: "#ffc21a",
  emerald: "#22c55e",
  ocean: "#3b82f6",
};

export const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

export function parseStoredSession() {
  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue);
    if (!parsed?.admin) {
      return parsed;
    }

    return {
      ...parsed,
      admin: {
        ...parsed.admin,
        permissions: normalizeAdminPermissions(parsed.admin.permissions),
        settings: {
          theme: String(parsed.admin.settings?.theme || "warm").trim(),
          customAccentColor:
            normalizeHexColor(parsed.admin.settings?.customAccentColor) || "",
        },
      },
    };
  } catch (error) {
    return null;
  }
}

export function getInitials(name) {
  return String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

export function normalizeHexColor(value) {
  const normalized = String(value || "").trim();
  return /^#([0-9a-fA-F]{6})$/.test(normalized) ? normalized.toLowerCase() : "";
}

function hexToRgb(hexColor) {
  const safeColor = normalizeHexColor(hexColor);
  if (!safeColor) return null;

  return {
    red: Number.parseInt(safeColor.slice(1, 3), 16),
    green: Number.parseInt(safeColor.slice(3, 5), 16),
    blue: Number.parseInt(safeColor.slice(5, 7), 16),
  };
}

function mixChannels(baseValue, targetValue, ratio) {
  return Math.round(baseValue + (targetValue - baseValue) * ratio);
}

function rgbToHex({ red, green, blue }) {
  return `#${[red, green, blue]
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`;
}

function lightenColor(hexColor, ratio) {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return "#ffc21a";

  return rgbToHex({
    red: mixChannels(rgb.red, 255, ratio),
    green: mixChannels(rgb.green, 255, ratio),
    blue: mixChannels(rgb.blue, 255, ratio),
  });
}

function darkenColor(hexColor, ratio) {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return "#f3b300";

  return rgbToHex({
    red: mixChannels(rgb.red, 0, ratio),
    green: mixChannels(rgb.green, 0, ratio),
    blue: mixChannels(rgb.blue, 0, ratio),
  });
}

export function resolveAdminAccentColor(settings = {}) {
  const themeKey = String(settings?.theme || "warm").trim();
  if (themeKey === "custom") {
    return normalizeHexColor(settings?.customAccentColor) || ADMIN_THEME_PRESETS.warm;
  }

  return ADMIN_THEME_PRESETS[themeKey] || ADMIN_THEME_PRESETS.warm;
}

export function applyAdminTheme(settings = {}) {
  if (typeof document === "undefined") return;

  const accent = resolveAdminAccentColor(settings);
  const rgb = hexToRgb(accent) || { red: 255, green: 194, blue: 26 };
  const root = document.documentElement;

  root.style.setProperty("--admin-brand", accent);
  root.style.setProperty("--admin-yellow", accent);
  root.style.setProperty("--admin-brand-rgb", `${rgb.red}, ${rgb.green}, ${rgb.blue}`);
  root.style.setProperty("--admin-brand-top", lightenColor(accent, 0.2));
  root.style.setProperty("--admin-brand-soft", `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, 0.16)`);
  root.style.setProperty(
    "--admin-brand-soft-strong",
    `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, 0.3)`
  );
  root.style.setProperty("--admin-brand-deep", darkenColor(accent, 0.08));
  root.style.setProperty("--admin-brand-strong", darkenColor(accent, 0.16));
  root.style.setProperty("--admin-brand-text", darkenColor(accent, 0.38));
}

export function toAbsoluteUrl(pathname) {
  if (!pathname) return "";
  if (pathname.startsWith("http")) return pathname;
  return `${API_BASE_URL}${pathname}`;
}

const PRESENCE_STATUS_META = {
  online: {
    label: "Online",
    icon: "check",
    className: "online",
  },
  right_back: {
    label: "Right back",
    icon: "clock",
    className: "right_back",
  },
  busy: {
    label: "Busy",
    icon: "",
    className: "busy",
  },
  out_of_office: {
    label: "Out of office",
    icon: "logOut",
    className: "out_of_office",
  },
};

const PROFILE_PRESENCE_KEYS = Object.keys(PRESENCE_STATUS_META);

export function getPresenceStatusMeta(status) {
  const safeStatus = String(status || "online").trim();
  return PRESENCE_STATUS_META[safeStatus] || PRESENCE_STATUS_META.online;
}

export function resolveEmployeeProfileStatus(source = {}) {
  const directProfileStatus = String(source?.profileStatus || "").trim();
  if (PROFILE_PRESENCE_KEYS.includes(directProfileStatus)) {
    return directProfileStatus;
  }

  const settingsPresenceStatus = String(source?.settings?.presenceStatus || "").trim();
  if (PROFILE_PRESENCE_KEYS.includes(settingsPresenceStatus)) {
    return settingsPresenceStatus;
  }

  const directPresenceStatus = String(source?.presenceStatus || "").trim();
  if (PROFILE_PRESENCE_KEYS.includes(directPresenceStatus)) {
    return directPresenceStatus;
  }

  return "online";
}

export function isThisMonth(dateString) {
  const parsedDate = new Date(dateString);
  if (Number.isNaN(parsedDate.getTime())) return false;

  const today = new Date();
  return (
    parsedDate.getMonth() === today.getMonth() &&
    parsedDate.getFullYear() === today.getFullYear()
  );
}

export function formatDateTimeLabel(value, fallback = "Never") {
  if (!value) return fallback;

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return fallback;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsedDate);
}

export function createEmployeeFormFromEmployee(employee) {
  return {
    ...INITIAL_EMPLOYEE_FORM,
    fullName: employee?.fullName || "",
    email: employee?.email || "",
    username: employee?.username || "",
    loginPassword: "",
    phoneNumber: employee?.phoneNumber || "",
    dateOfBirth: employee?.dateOfBirth || "",
    gender: employee?.gender || "",
    maritalStatus: employee?.maritalStatus || "",
    department: employee?.department || "",
    designation: employee?.designation || "",
    role: employee?.role || "",
    dateOfJoining: employee?.dateOfJoining || "",
    employmentType: employee?.employmentType || "",
    workShiftStart: employee?.workShiftStart || "09:00",
    workShiftEnd: employee?.workShiftEnd || "18:00",
    reportingManager: employee?.reportingManager || "",
    basicSalary: employee?.basicSalary ? String(employee.basicSalary) : "",
    houseRentAllowance: employee?.houseRentAllowance
      ? String(employee.houseRentAllowance)
      : "",
    conveyanceAllowance: employee?.conveyanceAllowance
      ? String(employee.conveyanceAllowance)
      : "",
    specialAllowance: employee?.specialAllowance
      ? String(employee.specialAllowance)
      : "",
    providentFund: employee?.providentFund ? String(employee.providentFund) : "",
    professionalTax: employee?.professionalTax
      ? String(employee.professionalTax)
      : "",
    incomeTax: employee?.incomeTax ? String(employee.incomeTax) : "",
    payFrequency: employee?.payFrequency || "",
    taxRegime: employee?.taxRegime || "Old Regime",
    bankName: employee?.bankName || "",
    accountNumber: employee?.accountNumber || "",
    ifscCode: employee?.ifscCode || "",
    panNumber: employee?.panNumber || "",
    uanNumber: employee?.uanNumber || "",
    pfNumber: employee?.pfNumber || "",
    addressLine1: employee?.addressLine1 || "",
    addressLine2: employee?.addressLine2 || "",
    city: employee?.city || "",
    state: employee?.state || "",
    pinCode: employee?.pinCode || "",
    country: employee?.country || "India",
  };
}

export function formatWorkingHoursLabel(startTime, endTime) {
  const safeStart = String(startTime || "").trim();
  const safeEnd = String(endTime || "").trim();

  if (!safeStart || !safeEnd) {
    return "—";
  }

  const startDate = new Date(`2000-01-01T${safeStart}:00`);
  const endDate = new Date(`2000-01-01T${safeEnd}:00`);

  const formatTime = (date, fallbackValue) =>
    Number.isNaN(date.getTime())
      ? fallbackValue
      : new Intl.DateTimeFormat("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }).format(date);

  return `${formatTime(startDate, safeStart)} - ${formatTime(endDate, safeEnd)}`;
}

export function buildEmployeeAddress(employee) {
  return [
    employee?.addressLine1,
    employee?.addressLine2,
    employee?.city,
    employee?.state,
    employee?.pinCode,
    employee?.country,
  ]
    .filter(Boolean)
    .join(", ");
}

export function createDepartmentFormFromDepartment(department) {
  return {
    ...INITIAL_DEPARTMENT_FORM,
    name: department?.name || "",
    description: department?.description || "",
    departmentHeadName: department?.departmentHeadName || "",
    departmentHeadTitle: department?.departmentHeadTitle || "",
    status: department?.status || "active",
  };
}

export function createDesignationFormFromDesignation(designation) {
  return {
    ...INITIAL_DESIGNATION_FORM,
    name: designation?.name || "",
    department: designation?.department || "",
    level: designation?.level || "L1",
    description: designation?.description || "",
    status: designation?.status || "active",
  };
}
